import type {
  ApiResponse,
  BellSchedule,
  BellScheduleFormData,
  Semester
} from '@/types'
import {
  calculateEndTime,
  createDefaultPeriod,
  DEFAULT_CONFIG,
  generatePeriodId,
  resolveFirstEveningStudyStartTime,
  type Period,
  type BellSchedulePeriods
} from '@/types/bell-schedule'
import {
  syncAfternoonToEveningBreak,
  syncMorningStudyToClassBreak
} from '@/utils/bell-schedule-bridges'
import { http } from './request'

function expectRecord(value: unknown, fieldName: string): Record<string, unknown> {
  if (typeof value !== 'object' || value == null || Array.isArray(value)) {
    throw new Error(`Invalid ${fieldName}: expected object`)
  }
  return value as Record<string, unknown>
}

function expectString(value: unknown, fieldName: string): string {
  if (typeof value !== 'string') {
    throw new Error(`Invalid ${fieldName}: expected string`)
  }
  return value
}

function expectNumber(value: unknown, fieldName: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`Invalid ${fieldName}: expected number`)
  }
  return value
}

function expectNullableNumber(value: unknown, fieldName: string): number | null {
  if (value == null) {
    return null
  }
  return expectNumber(value, fieldName)
}

/** 大课间位置：优先 periods，其次 config（AI/旧数据可能只写在 config） */
function resolveBreakPosition(
  periodsRaw: Record<string, unknown>,
  configRaw: Record<string, unknown>,
  field: 'morningBreakPosition' | 'afternoonBreakPosition'
): number {
  const fromPeriods = periodsRaw[field]
  if (typeof fromPeriods === 'number' && Number.isFinite(fromPeriods)) {
    return fromPeriods
  }

  const fromConfig = configRaw[field]
  if (typeof fromConfig === 'number' && Number.isFinite(fromConfig)) {
    return fromConfig
  }

  // 默认大课间位置：第 1 节后（0 基：0 = 第 1 节后）
  return 0
}

function expectOptionalString(value: unknown, fieldName: string): string | undefined {
  if (value == null) {
    return undefined
  }
  return expectString(value, fieldName)
}

function expectNumberArray(value: unknown, fieldName: string): number[] {
  if (!Array.isArray(value)) {
    throw new Error(`Invalid ${fieldName}: expected number array`)
  }
  return value.map((item, index) => expectNumber(item, `${fieldName}[${index}]`))
}

function buildDefaultBellSchedulePeriods(config: typeof DEFAULT_CONFIG): BellSchedulePeriods {
  const periods: BellSchedulePeriods = {
    morningStudy: [],
    morningShortBreaks: [],
    morningStudyToClassBreak: null,
    morning: [],
    morningBreak: null,
    morningBreakPosition: 0,
    lunchBreak: null,
    afternoon: [],
    afternoonShortBreaks: [],
    afternoonBreak: null,
    afternoonBreakPosition: 0,
    afternoonToEveningBreak: null,
    eveningStudy: [],
    eveningStudyShortBreaks: []
  }

  let currentTime = '07:00'

  for (let i = 0; i < config.morningStudy; i++) {
    const id = generatePeriodId()
    periods.morningStudy.push(
      createDefaultPeriod(id, `早自习 ${i + 1}`, 'study', currentTime, config.morningStudyDuration)
    )
    currentTime = calculateEndTime(currentTime, config.morningStudyDuration)

    if (i < config.morningStudy - 1) {
      const breakId = generatePeriodId()
      periods.morningShortBreaks.push(
        createDefaultPeriod(breakId, '课间休息', 'break', currentTime, config.breakDuration)
      )
      currentTime = calculateEndTime(currentTime, config.breakDuration)
    }
  }

  let classStartAnchor = config.classStartTime
  if (config.morningStudy > 0 && periods.morningStudy.length > 0) {
    const lastMorningStudyEnd = periods.morningStudy[periods.morningStudy.length - 1].endTime
    classStartAnchor = calculateEndTime(lastMorningStudyEnd, config.breakDuration)
  }

  syncMorningStudyToClassBreak(periods, periods, classStartAnchor, config.morningPeriods)

  currentTime = classStartAnchor
  for (let i = 0; i < config.morningPeriods; i++) {
    const id = generatePeriodId()
    periods.morning.push(
      createDefaultPeriod(id, `第${i + 1}节`, 'course', currentTime, config.classDuration)
    )
    currentTime = calculateEndTime(currentTime, config.classDuration)

    if (i === periods.morningBreakPosition && config.morningPeriods > 1) {
      const breakId = generatePeriodId()
      periods.morningBreak = createDefaultPeriod(
        breakId,
        '大课间',
        'morning-exercise',
        currentTime,
        config.morningLongBreakDuration
      )
      currentTime = calculateEndTime(currentTime, config.morningLongBreakDuration)
    } else if (i < config.morningPeriods - 1) {
      const breakId = generatePeriodId()
      periods.morningShortBreaks.push(
        createDefaultPeriod(breakId, '课间休息', 'break', currentTime, config.breakDuration)
      )
      currentTime = calculateEndTime(currentTime, config.breakDuration)
    }
  }

  const lunchId = generatePeriodId()
  periods.lunchBreak = createDefaultPeriod(
    lunchId,
    '午休',
    'lunch',
    currentTime,
    config.lunchBreakDuration
  )

  currentTime = periods.lunchBreak.endTime
  for (let i = 0; i < config.afternoonPeriods; i++) {
    const id = generatePeriodId()
    periods.afternoon.push(
      createDefaultPeriod(
        id,
        `第${config.morningPeriods + i + 1}节`,
        'course',
        currentTime,
        config.classDuration
      )
    )
    currentTime = calculateEndTime(currentTime, config.classDuration)

    if (i === periods.afternoonBreakPosition && config.afternoonPeriods > 1) {
      const breakId = generatePeriodId()
      periods.afternoonBreak = createDefaultPeriod(
        breakId,
        '大课间',
        'morning-exercise',
        currentTime,
        config.afternoonLongBreakDuration
      )
      currentTime = calculateEndTime(currentTime, config.afternoonLongBreakDuration)
    } else if (i < config.afternoonPeriods - 1) {
      const breakId = generatePeriodId()
      periods.afternoonShortBreaks.push(
        createDefaultPeriod(breakId, '课间休息', 'break', currentTime, config.breakDuration)
      )
      currentTime = calculateEndTime(currentTime, config.breakDuration)
    }
  }

  if (config.eveningStudy > 0) {
    const eveningStartTime =
      config.afternoonPeriods > 0 && periods.afternoon.length > 0
        ? resolveFirstEveningStudyStartTime(
            currentTime,
            periods.afternoon[periods.afternoon.length - 1].endTime,
            config.breakDuration
          )
        : calculateEndTime(currentTime, config.breakDuration)
    syncAfternoonToEveningBreak(periods, periods, config, eveningStartTime)

    currentTime = eveningStartTime
    for (let i = 0; i < config.eveningStudy; i++) {
      const id = generatePeriodId()
      periods.eveningStudy.push(
        createDefaultPeriod(id, `晚自习 ${i + 1}`, 'study', currentTime, config.eveningStudyDuration)
      )
      currentTime = calculateEndTime(currentTime, config.eveningStudyDuration)

      if (i < config.eveningStudy - 1) {
        const breakId = generatePeriodId()
        periods.eveningStudyShortBreaks.push(
          createDefaultPeriod(breakId, '课间休息', 'break', currentTime, config.breakDuration)
        )
        currentTime = calculateEndTime(currentTime, config.breakDuration)
      }
    }
  }

  return periods
}

function toBellSchedule(rawDto: Record<string, unknown>): BellSchedule {
  const raw = expectRecord(rawDto, 'bellSchedule')
  const config = expectRecord(raw.config, 'bellSchedule.config')
  const periodsRaw = expectRecord(raw.periods, 'bellSchedule.periods')

  function toPeriodArray(arr: unknown, fieldName: string): Period[] {
    // Older persisted zero-count segments could omit the key. Treat that one compatible
    // representation as an empty segment, while still rejecting malformed non-array values.
    if (arr == null) {
      return []
    }
    if (!Array.isArray(arr)) {
      throw new Error(`Invalid ${fieldName}: expected array`)
    }
    return arr.map((item: unknown, index) => {
      const p = expectRecord(item, `${fieldName}[${index}]`)
      return {
        id: expectString(p.id, `${fieldName}[${index}].id`),
        name: expectString(p.name, `${fieldName}[${index}].name`),
        type: expectString(p.type, `${fieldName}[${index}].type`) as Period['type'],
        startTime: expectString(p.startTime, `${fieldName}[${index}].startTime`),
        endTime: expectString(p.endTime, `${fieldName}[${index}].endTime`),
        duration: expectNumber(p.duration, `${fieldName}[${index}].duration`)
      }
    })
  }

  function toPeriodOrNull(val: unknown, fieldName: string): Period | null {
    if (val == null) return null
    const p = expectRecord(val, fieldName)
    return {
      id: expectString(p.id, `${fieldName}.id`),
      name: expectString(p.name, `${fieldName}.name`),
      type: expectString(p.type, `${fieldName}.type`) as Period['type'],
      startTime: expectString(p.startTime, `${fieldName}.startTime`),
      endTime: expectString(p.endTime, `${fieldName}.endTime`),
      duration: expectNumber(p.duration, `${fieldName}.duration`)
    }
  }

  const periods: BellSchedulePeriods = {
    morningStudy: toPeriodArray(periodsRaw.morningStudy, 'bellSchedule.periods.morningStudy'),
    morningShortBreaks: toPeriodArray(periodsRaw.morningShortBreaks, 'bellSchedule.periods.morningShortBreaks'),
    morningStudyToClassBreak: toPeriodOrNull(periodsRaw.morningStudyToClassBreak, 'bellSchedule.periods.morningStudyToClassBreak'),
    morning: toPeriodArray(periodsRaw.morning, 'bellSchedule.periods.morning'),
    morningBreak: toPeriodOrNull(periodsRaw.morningBreak, 'bellSchedule.periods.morningBreak'),
    morningBreakPosition: resolveBreakPosition(periodsRaw, config, 'morningBreakPosition'),
    lunchBreak: toPeriodOrNull(periodsRaw.lunchBreak, 'bellSchedule.periods.lunchBreak'),
    afternoon: toPeriodArray(periodsRaw.afternoon, 'bellSchedule.periods.afternoon'),
    afternoonShortBreaks: toPeriodArray(periodsRaw.afternoonShortBreaks, 'bellSchedule.periods.afternoonShortBreaks'),
    afternoonBreak: toPeriodOrNull(periodsRaw.afternoonBreak, 'bellSchedule.periods.afternoonBreak'),
    afternoonBreakPosition: resolveBreakPosition(periodsRaw, config, 'afternoonBreakPosition'),
    afternoonToEveningBreak: toPeriodOrNull(periodsRaw.afternoonToEveningBreak, 'bellSchedule.periods.afternoonToEveningBreak'),
    eveningStudy: toPeriodArray(periodsRaw.eveningStudy, 'bellSchedule.periods.eveningStudy'),
    eveningStudyShortBreaks: toPeriodArray(periodsRaw.eveningStudyShortBreaks, 'bellSchedule.periods.eveningStudyShortBreaks')
  }

  return {
    id: expectNumber(raw.id, 'bellSchedule.id'),
    name: expectString(raw.name, 'bellSchedule.name'),
    createdAt: expectString(raw.createdAt, 'bellSchedule.createdAt'),
    updatedAt: expectString(raw.updatedAt, 'bellSchedule.updatedAt'),
    semesterId: expectNullableNumber(raw.semesterId, 'bellSchedule.semesterId'),
    semesterName: expectOptionalString(raw.semesterName, 'bellSchedule.semesterName'),
    structureEditable: raw.structureEditable !== false,
    structureLockReason:
      typeof raw.structureLockReason === 'string' ? raw.structureLockReason : null,
    config: {
      weekdays: expectNumberArray(config.weekdays, 'bellSchedule.config.weekdays'),
      activeCellKeys: Array.isArray(config.activeCellKeys)
        ? (config.activeCellKeys as unknown[]).filter(
            (item): item is string => typeof item === 'string' && item.length > 0
          )
        : undefined,
      totalPeriods: expectNumber(config.totalPeriods, 'bellSchedule.config.totalPeriods'),
      morningPeriods: expectNumber(config.morningPeriods, 'bellSchedule.config.morningPeriods'),
      afternoonPeriods: expectNumber(config.afternoonPeriods, 'bellSchedule.config.afternoonPeriods'),
      morningStudy: expectNumber(config.morningStudy, 'bellSchedule.config.morningStudy'),
      eveningStudy: expectNumber(config.eveningStudy, 'bellSchedule.config.eveningStudy'),
      classStartTime: expectString(config.classStartTime, 'bellSchedule.config.classStartTime'),
      morningStudyDuration: expectNumber(config.morningStudyDuration, 'bellSchedule.config.morningStudyDuration'),
      eveningStudyDuration: expectNumber(config.eveningStudyDuration, 'bellSchedule.config.eveningStudyDuration'),
      classDuration: expectNumber(config.classDuration, 'bellSchedule.config.classDuration'),
      breakDuration: expectNumber(config.breakDuration, 'bellSchedule.config.breakDuration'),
      morningLongBreakDuration: expectNumber(config.morningLongBreakDuration, 'bellSchedule.config.morningLongBreakDuration'),
      afternoonLongBreakDuration: expectNumber(config.afternoonLongBreakDuration, 'bellSchedule.config.afternoonLongBreakDuration'),
      lunchBreakDuration: expectNumber(config.lunchBreakDuration, 'bellSchedule.config.lunchBreakDuration')
    },
    periods
  }
}

function serializePeriods(periods: BellSchedulePeriods): Record<string, unknown> {
  return {
    morningStudy: periods.morningStudy.map(p => ({ ...p })),
    morningShortBreaks: periods.morningShortBreaks.map(p => ({ ...p })),
    morningStudyToClassBreak: periods.morningStudyToClassBreak
      ? { ...periods.morningStudyToClassBreak }
      : null,
    morning: periods.morning.map(p => ({ ...p })),
    morningBreak: periods.morningBreak ? { ...periods.morningBreak } : null,
    morningBreakPosition: periods.morningBreakPosition,
    lunchBreak: periods.lunchBreak ? { ...periods.lunchBreak } : null,
    afternoon: periods.afternoon.map(p => ({ ...p })),
    afternoonShortBreaks: periods.afternoonShortBreaks.map(p => ({ ...p })),
    afternoonBreak: periods.afternoonBreak ? { ...periods.afternoonBreak } : null,
    afternoonBreakPosition: periods.afternoonBreakPosition,
    afternoonToEveningBreak: periods.afternoonToEveningBreak
      ? { ...periods.afternoonToEveningBreak }
      : null,
    eveningStudy: periods.eveningStudy.map(p => ({ ...p })),
    eveningStudyShortBreaks: periods.eveningStudyShortBreaks.map(p => ({ ...p }))
  }
}

type RawDto = Record<string, unknown>

export const bellScheduleApi = {
  async listBellSchedules(semesterId?: number): Promise<ApiResponse<BellSchedule[]>> {
    const params =
      semesterId != null && Number.isFinite(semesterId) ? { semesterId } : undefined
    const response = await http.get<RawDto[]>('/bell-schedules', params)
    return {
      success: true,
      code: response.code,
      message: response.message,
      data: response.data.map(s => toBellSchedule(expectRecord(s, 'bellScheduleListItem'))),
      traceId: response.traceId,
      timestamp: response.timestamp
    }
  },

  async createBellSchedule(data: BellScheduleFormData): Promise<ApiResponse<BellSchedule>> {
    const response = await http.post<RawDto>('/bell-schedules', {
      name: data.name,
      config: data.config,
      periods: serializePeriods(data.periods)
    })
    return {
      success: true,
      code: response.code,
      message: response.message,
      data: toBellSchedule(response.data),
      traceId: response.traceId,
      timestamp: response.timestamp
    }
  },

  async updateBellSchedule(id: number, data: Partial<BellScheduleFormData>): Promise<ApiResponse<BellSchedule>> {
    const payload: Record<string, unknown> = {}
    if (data.name !== undefined) payload.name = data.name
    if (data.config !== undefined) payload.config = data.config
    if (data.periods !== undefined) payload.periods = serializePeriods(data.periods)

    const response = await http.put<RawDto>(`/bell-schedules/${id}`, payload)
    return {
      success: true,
      code: response.code,
      message: response.message,
      data: toBellSchedule(response.data),
      traceId: response.traceId,
      timestamp: response.timestamp
    }
  },

  async copyBellSchedule(id: number): Promise<ApiResponse<BellSchedule>> {
    const response = await http.post<RawDto>(`/bell-schedules/${id}/copy`)
    return {
      success: true,
      code: response.code,
      message: response.message,
      data: toBellSchedule(response.data),
      traceId: response.traceId,
      timestamp: response.timestamp
    }
  },

  async applyBellScheduleToSemester(bellScheduleId: number, semesterId: number): Promise<ApiResponse<BellSchedule>> {
    const response = await http.post<RawDto>(
      `/bell-schedules/${bellScheduleId}/apply-to-semester/${semesterId}`
    )
    return {
      success: true,
      code: response.code,
      message: response.message,
      data: toBellSchedule(response.data),
      traceId: response.traceId,
      timestamp: response.timestamp
    }
  },

  async listSemesters(): Promise<ApiResponse<Semester[]>> {
    const response = await http.get<RawDto[]>('/academic/semesters')
    return {
      success: true,
      code: response.code,
      message: response.message,
      data: response.data as unknown as Semester[],
      traceId: response.traceId,
      timestamp: response.timestamp
    }
  },

  buildDefaultBellSchedulePeriods
}
